import React, { useState, useEffect } from 'react';
import {
  Grid,
  View,
  Flex,
  Link,
  Item,
  Text,
  MenuTrigger,
  Menu,
  ActionButton,
} from '@adobe/react-spectrum';
import PropTypes from 'prop-types';
import { Tooltip } from '@material-ui/core';
import Fade from '@material-ui/core/Fade';
import { BsChevronDown, BsChevronRight } from 'react-icons/bs';
import More from '@spectrum-icons/workflow/More';
import moment from 'moment';
import getUserInitials from '../../utils/getUserInitials';
import { getLocalDateTime } from '../../utils/utils';
import actionWebInvoke from '../../utils/actionCallUtils';
import actions from '../../config.json';
import OBJCODE from '../../constants';

import {
  ProgressBarContainer,
  ContainerStyles,
  ProjectInfo,
  IconDiv,
  ProgressBar,
  TaskSide,
  PercentageAndDaysSide,
  PercentageInfo,
  DaysInfo,
  BottomWrapper,
  InProgressAvatarAndPercentage,
  BottomDetailsContainer,
  BottomDateDisplay,
  ColumnInfo,
  CompletedTasks,
  InProgressTasks,
  UpNextTasks,
  TaskToDisplay,
  Avatar,
  Icon,
  ReportIcon,
} from './styles';

const ProjectProgressBar = ({
  id,
  dueDate,
  completed,
  projectName,
  daysLeft,
  getParentId,
  workfrontEndpoint,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [sortedTaksByCompletion, setSortedTaksByCompletion] = useState({});
  const [projectTask, setProjectTask] = useState([]);

  async function taskOverview(projectId) {
    const fields = [
      'actualCompletionDate',
      'status',
      'plannedStartDate',
      'percentComplete',
      'assignmentsListString',
      'assignments:assignedTo',
      'taskNumber',
      'project:program',
      'project:portfolio',
    ];

    const queryParams = {
      numberOfChildren: '0',
      projectID: projectId,
      taskNumber_Sort: 'ASC',
    };

    const actionResponse = await actionWebInvoke(
      actions['workfront-action'],
      {},
      { objCode: OBJCODE.TASK_SEARCH, queryParams, fields },
    );
    setProjectTask(actionResponse);
  }
  useEffect(() => {
    const tasksCompleted = [];
    const tasksInProgress = [];
    const tasksUpNext = [];
    if (isOpen) {
      taskOverview(id);
      if (projectTask) {
        projectTask?.map(el => {
          if (el.status === 'INP') return tasksInProgress.push(el);
          if (el.status === 'NEW') return tasksUpNext.push(el);
          return tasksCompleted.push(el);
        });
      }
    }
    return setSortedTaksByCompletion({
      numberOfTasksCompleted: tasksCompleted.length,
      numberOfTasksInProgress: tasksInProgress.length,
      numberOfTasksUpNext: tasksUpNext.length,
      completed: tasksCompleted.reverse().slice(0, 3),
      inProgress: tasksInProgress.slice(0, 3),
      upNext: tasksUpNext.slice(0, 3),
    });
  }, [isOpen]);

  useEffect(() => {
    taskOverview(id);
  }, []);

  const numberOfTasks = Object.values(sortedTaksByCompletion).filter(
    el => el.length,
  );

  const sortMaxLengthOfTasks = Math.max.apply(
    null,
    Object.values(numberOfTasks).map(el => el.length),
  );

  const totalHeight = sortMaxLengthOfTasks * 90;

  return (
    <Grid>
      <View>
        <ProgressBarContainer onClick={() => setIsOpen(!isOpen)}>
          <ContainerStyles totalHeight={sortMaxLengthOfTasks} isOpen={isOpen}>
            <ProjectInfo>
              <IconDiv>
                {isOpen ? (
                  <BsChevronDown size={10} style={{ strokeWidth: 2 }} />
                ) : (
                  <BsChevronRight size={10} style={{ strokeWidth: 2 }} />
                )}
              </IconDiv>
              <Tooltip
                TransitionComponent={Fade}
                TransitionProps={{ timeout: 600 }}
                arrow
                placement="top-end"
                title={projectName}
              >
                <TaskSide>{projectName}</TaskSide>
              </Tooltip>
            </ProjectInfo>
            {isOpen ? (
              <BottomWrapper>
                {getParentId(id)}
                <BottomDateDisplay>
                  {`Due on: ${moment(getLocalDateTime(dueDate)).format(
                    'MMM D, YYYY',
                  )}`}
                  <i>
                    {moment(dueDate.split('T')[0]).format('YYYY-MM-DD') <
                      moment().toISOString().split('T')[0] && 'Overdue'}
                  </i>
                </BottomDateDisplay>
                <BottomDetailsContainer height={totalHeight}>
                  <UpNextTasks>
                    <ColumnInfo>
                      Up next ({sortedTaksByCompletion.numberOfTasksUpNext})
                    </ColumnInfo>
                    <section>
                      {sortedTaksByCompletion ? (
                        sortedTaksByCompletion.upNext.map((task, index) => (
                          <>
                            <Tooltip
                              TransitionComponent={Fade}
                              TransitionProps={{ timeout: 600 }}
                              placement="top-start"
                              title={task.name}
                            >
                              <TaskToDisplay key={task.ID + index}>
                                {task.name}
                              </TaskToDisplay>
                            </Tooltip>
                          </>
                        ))
                      ) : (
                        <div />
                      )}
                    </section>
                  </UpNextTasks>
                  <InProgressTasks>
                    <ColumnInfo>
                      In progress (
                      {sortedTaksByCompletion.numberOfTasksInProgress})
                    </ColumnInfo>
                    <section>
                      {sortedTaksByCompletion ? (
                        sortedTaksByCompletion.inProgress.map((task, index) => (
                          <>
                            <Tooltip
                              TransitionComponent={Fade}
                              TransitionProps={{ timeout: 600 }}
                              placement="top-start"
                              title={task.name}
                            >
                              <TaskToDisplay
                                key={task.ID + index}
                                column="inProgress"
                              >
                                {task.name}
                              </TaskToDisplay>
                            </Tooltip>
                            <InProgressAvatarAndPercentage>
                              <Flex alignItems="center">
                                {task.assignments &&
                                  task.assignments.map(user =>
                                    user?.assignedTo ? (
                                      <Tooltip
                                        TransitionComponent={Fade}
                                        TransitionProps={{ timeout: 600 }}
                                        placement="top-start"
                                        title={user.assignedTo.name}
                                      >
                                        <Avatar>
                                          {getUserInitials(
                                            user.assignedTo.name,
                                          )}
                                        </Avatar>
                                      </Tooltip>
                                    ) : null,
                                  )}
                              </Flex>
                              <span>{task.percentComplete}% Complete</span>
                            </InProgressAvatarAndPercentage>
                          </>
                        ))
                      ) : (
                        <div />
                      )}
                    </section>
                  </InProgressTasks>

                  <CompletedTasks>
                    <ColumnInfo>
                      Completed ({sortedTaksByCompletion.numberOfTasksCompleted}
                      )
                    </ColumnInfo>
                    <section>
                      {sortedTaksByCompletion ? (
                        sortedTaksByCompletion.completed.map((task, index) => (
                          <>
                            <Tooltip
                              TransitionComponent={Fade}
                              TransitionProps={{ timeout: 600 }}
                              placement="top-start"
                              title={task.name}
                            >
                              <TaskToDisplay key={task.ID + index}>
                                {task.name}
                              </TaskToDisplay>
                            </Tooltip>
                          </>
                        ))
                      ) : (
                        <div />
                      )}
                    </section>
                  </CompletedTasks>
                </BottomDetailsContainer>
              </BottomWrapper>
            ) : (
              <div />
            )}
            <ProgressBar completed={completed} />
          </ContainerStyles>
          <div
            style={{
              display: 'flex',
              alignSelf: 'flex-start',
            }}
          >
            <PercentageAndDaysSide daysLeft={daysLeft}>
              <PercentageInfo>{`${completed}%`}</PercentageInfo>
              <DaysInfo>{daysLeft < 0 ? '0d' : `${daysLeft}d`}</DaysInfo>
            </PercentageAndDaysSide>
            <MenuTrigger>
              <ActionButton isQuiet UNSAFE_style={{ alignSelf: 'center' }}>
                <More />
              </ActionButton>
              <Menu>
                <Item>
                  <Icon />
                  <Text marginX="25px">
                    <Link variant="secondary" isQuiet>
                      <a
                        href={`${workfrontEndpoint}/project/${id}/overview`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Workfront
                      </a>
                    </Link>
                  </Text>
                </Item>
                <Item key="report" textValue="report">
                  <ReportIcon />
                  <Text marginX="25px">Milestone Report</Text>
                </Item>
              </Menu>
            </MenuTrigger>
          </div>
        </ProgressBarContainer>
      </View>
    </Grid>
  );
};

ProjectProgressBar.propTypes = {
  completed: PropTypes.string.isRequired,
  projectName: PropTypes.string.isRequired,
  daysLeft: PropTypes.number.isRequired,
  dueDate: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  workfrontEndpoint: PropTypes.string.isRequired,
  getParentId: PropTypes.string.isRequired,
};

export default ProjectProgressBar;
